"use client";

import { SocialSharing } from "@repo/ui/social-sharing";
import { CircleAlert, Download, Loader2, Sparkles, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import {
  buildProductShareHashtags,
  buildProductShareUrl,
  getPrimaryShareTag,
} from "@/features/products/shared/socialShare";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  HStack,
  Label,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  VStack,
} from "@/ui";
import {
  useVirtualTryOnJobsStore,
  type VirtualTryOnJobInput,
} from "../store/useVirtualTryOnJobsStore";
import type { VirtualTryOnJob } from "../virtualTryOnJobState";
import { TryOnPreviewZoom } from "./TryOnPreviewZoom";

type VirtualTryOnJobsContextValue = {
  submitJob: (input: VirtualTryOnJobInput) => void;
};

const VirtualTryOnJobsContext = createContext<VirtualTryOnJobsContextValue | null>(null);

function previewToBlob(job: VirtualTryOnJob): Blob | null {
  if (!(job.previewBase64 && job.mimeType)) return null;
  const binary = atob(job.previewBase64);
  return new Blob([Uint8Array.from(binary, (char) => char.charCodeAt(0))], {
    type: job.mimeType,
  });
}

export function useVirtualTryOnJobs(): VirtualTryOnJobsContextValue {
  const context = useContext(VirtualTryOnJobsContext);
  if (!context) throw new Error("useVirtualTryOnJobs must be used within VirtualTryOnJobsProvider");
  return context;
}

export function VirtualTryOnJobsProvider({ children }: { children: ReactNode }) {
  const t = useTranslations("virtual_try_on");
  const errorsT = useTranslations("errors");
  const jobs = useVirtualTryOnJobsStore((state) => state.jobs);
  const selectedJobId = useVirtualTryOnJobsStore((state) => state.selectedJobId);
  const setSelectedJobId = useVirtualTryOnJobsStore((state) => state.setSelectedJobId);
  const setAwaitingResultDialog = useVirtualTryOnJobsStore(
    (state) => state.setAwaitingResultDialog,
  );
  const removeJob = useVirtualTryOnJobsStore((state) => state.removeJob);
  const submitJob = useVirtualTryOnJobsStore((state) => state.submitJob);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const selectedJob = jobs.find((job) => job.id === selectedJobId) ?? null;
  const activeJob = jobs.find((job) => job.status === "generating") ?? jobs[0] ?? null;
  const value = useMemo(() => ({ submitJob }), [submitJob]);

  useEffect(() => {
    setConsentChecked(false);
    setIsSharing(false);
    setShared(false);
    setShareError(null);
  }, [selectedJobId]);

  const shareWithCommunity = async () => {
    if (!(selectedJob?.shareToken && consentChecked)) return;
    const preview = previewToBlob(selectedJob);
    if (!preview) return;

    setIsSharing(true);
    setShareError(null);
    try {
      const form = new FormData();
      form.set("sku", selectedJob.sku);
      form.set("locale", selectedJob.locale);
      form.set("variantId", selectedJob.variantId);
      form.set("preview", preview, "virtual-try-on.jpg");
      form.set("previewToken", selectedJob.shareToken);
      const response = await fetch(`/${selectedJob.locale}/api/virtual-try-on/consent`, {
        method: "POST",
        body: form,
      });
      const data = (await response.json()) as { error?: string };
      if (!response.ok) {
        setShareError(data.error ?? "errors.virtual_try_on_upstream");
        return;
      }
      setShared(true);
    } catch {
      setShareError("errors.virtual_try_on_upstream");
    } finally {
      setIsSharing(false);
    }
  };

  const downloadPreview = () => {
    if (!selectedJob) return;
    const preview = previewToBlob(selectedJob);
    if (!preview) return;
    const url = URL.createObjectURL(preview);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedJob.sku}-virtual-try-on.${selectedJob.mimeType?.split("/")[1] || "jpg"}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const resolveError = (errorKey?: string | null) =>
    errorKey?.startsWith("errors.") ? errorsT(errorKey.replace("errors.", "")) : errorKey;
  const jobLabel =
    activeJob?.status === "generating"
      ? t("job_generating")
      : activeJob?.status === "completed"
        ? t("job_ready")
        : t("job_failed");

  return (
    <VirtualTryOnJobsContext.Provider value={value}>
      {children}
      {activeJob ? (
        <div className="fixed right-4 bottom-[4.5rem] z-50 md:right-6 md:bottom-20">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="icon-lg"
                  className="rounded-full shadow-lg"
                  aria-label={jobLabel}
                  data-testid="virtual-try-on-job-launcher"
                  onClick={() => setSelectedJobId(activeJob.id)}
                >
                  {activeJob.status === "generating" ? (
                    <Loader2 className="size-5 animate-spin motion-reduce:animate-none" />
                  ) : activeJob.status === "failed" ? (
                    <CircleAlert className="size-5" />
                  ) : (
                    <Sparkles className="size-5" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="left"
                sideOffset={8}
              >
                {jobLabel}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : null}
      <Dialog
        open={selectedJobId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedJobId(null);
            setAwaitingResultDialog(false);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedJob?.productName}</DialogTitle>
          </DialogHeader>
          {selectedJob?.status === "generating" ? (
            <VStack
              className="items-center gap-3 py-8 text-center"
              role="status"
            >
              <Loader2 className="size-6 animate-spin motion-reduce:animate-none" />
              <Text className="text-sm">{t("job_generating")}</Text>
              <Text className="text-xs text-muted-foreground">{t("generating_duration_hint")}</Text>
            </VStack>
          ) : null}
          {selectedJob?.status === "failed" ? (
            <VStack className="gap-4">
              <Text className="text-sm text-destructive">{resolveError(selectedJob.errorKey)}</Text>
              <Button
                type="button"
                variant="outline"
                onClick={() => removeJob(selectedJob.id)}
              >
                <X className="mr-2 size-4" />
                {t("job_dismiss")}
              </Button>
            </VStack>
          ) : null}
          {selectedJob?.status === "completed" &&
          selectedJob.previewBase64 &&
          selectedJob.mimeType ? (
            <VStack className="gap-4">
              <TryOnPreviewZoom
                src={`data:${selectedJob.mimeType};base64,${selectedJob.previewBase64}`}
                alt={t("preview_alt")}
              />
              {!shared ? (
                <VStack className="gap-4 rounded-sm border border-border/50 bg-muted/10 p-4">
                  <HStack className="items-start gap-2">
                    <Checkbox
                      id="try-on-job-consent"
                      checked={consentChecked}
                      onCheckedChange={(checked) => setConsentChecked(checked === true)}
                    />
                    <Label
                      htmlFor="try-on-job-consent"
                      className="cursor-pointer text-sm leading-snug"
                    >
                      {t("consent_label")}
                    </Label>
                  </HStack>
                  <Text className="text-xs text-muted-foreground">{t("consent_description")}</Text>
                  <Button
                    type="button"
                    disabled={!consentChecked || isSharing}
                    onClick={shareWithCommunity}
                  >
                    {isSharing ? t("sharing") : t("share_cta")}
                  </Button>
                </VStack>
              ) : (
                <Text
                  role="status"
                  aria-live="polite"
                  className="text-sm text-green-600"
                >
                  {t("shared_success")}
                </Text>
              )}
              {shareError ? (
                <Text className="text-sm text-destructive">{resolveError(shareError)}</Text>
              ) : null}
              <HStack className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={downloadPreview}
                >
                  <Download className="mr-2 size-4" />
                  {t("download_preview")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => removeJob(selectedJob.id)}
                >
                  {t("done_without_sharing")}
                </Button>
              </HStack>
              <SocialSharing
                url={buildProductShareUrl(selectedJob.locale, selectedJob.sku)}
                title={selectedJob.productName}
                hashtags={buildProductShareHashtags(selectedJob.sku, ["virtualtryon"])}
                primaryHashtag={getPrimaryShareTag(
                  buildProductShareHashtags(selectedJob.sku, ["virtualtryon"]),
                )}
                className="w-full flex-wrap items-center gap-2"
                image={`data:${selectedJob.mimeType};base64,${selectedJob.previewBase64}`}
                description={selectedJob.productName}
                label={t("share_cta")}
              />
            </VStack>
          ) : null}
        </DialogContent>
      </Dialog>
    </VirtualTryOnJobsContext.Provider>
  );
}
